import type { EventResponseData } from '../lib/api-client'
import type { Sportsbook } from '@/types/types'
import { useState } from 'react'
import Bet365Logo from '../assets/bet365.svg?react'
import BetMGMLogo from '../assets/betmgm.svg?react'
import DraftKingsLogo from '../assets/draftkings.svg?react'

import FanDuelLogo from '../assets/fanduel.svg?react'

type SelectionHandler = (
  selectionIndex: number,
  sportsbook: Sportsbook
) => void

const sportsbookConfig = {
  FanDuel: { color: 'bg-fanduel-secondary', logo: FanDuelLogo },
  Bet365: { color: 'bg-bet365-secondary', logo: Bet365Logo },
  DraftKings: { color: 'bg-draftkings-secondary', logo: DraftKingsLogo },
  BetMGM: { color: 'bg-betmgm-secondary', logo: BetMGMLogo },
}

const availableSportsbooks = [
  'FanDuel',
  'BetMGM',
  'Bet365',
  'DraftKings',
] as const

function SportsbookCheckbox({
  sportsbook,
  isSelected,
  onChange,
}: {
  sportsbook: Sportsbook
  isSelected: boolean
  onChange: (sportsbook: Sportsbook, checked: boolean) => void
}) {
  const config = sportsbookConfig[sportsbook]
  const LogoComponent = config.logo

  return (
    <button
      type="button"
      onClick={() => onChange(sportsbook, !isSelected)}
      className={`
        relative flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all duration-200 
        hover:scale-105 active:scale-95 cursor-pointer min-w-0 flex-shrink-0
        ${isSelected
      ? 'border-blue-500 bg-blue-50 shadow-md'
      : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
    }
      `}
      aria-label={`${isSelected ? 'Unselect' : 'Select'} ${sportsbook}`}
    >
      {/* Custom checkbox indicator */}
      <div className={`
        w-4 h-4 rounded border-2 flex items-center justify-center transition-colors duration-200
        ${isSelected
      ? 'bg-blue-500 border-blue-500'
      : 'bg-white border-gray-300'
    }
      `}
      >
        {isSelected && (
          <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        )}
      </div>

      {/* Logo */}
      <LogoComponent className="size-6 flex-shrink-0" />

    </button>
  )
}

function Widget({
  leagueName,
  markets,
  selectedMarketIndex,
  onMarketChange,
  oddsFormat,
  onToggleOddsFormat,
  onSelectionClick,
}: {
  leagueName: string
  markets: EventResponseData
  selectedMarketIndex: number
  onMarketChange: (marketIndex: number) => void
  oddsFormat: 'american' | 'decimal'
  onToggleOddsFormat: () => void
  onSelectionClick: SelectionHandler
}) {
  // Initialize with all sportsbooks selected
  const [selectedSportsbooks, setSelectedSportsbooks] = useState<Set<Sportsbook>>(
    () => new Set(availableSportsbooks),
  )

  const widgetData = markets[selectedMarketIndex]

  const handleOddsFormatChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const newFormat = event.target.value as 'american' | 'decimal'
    if (newFormat !== oddsFormat) {
      onToggleOddsFormat()
    }
  }

  const handleMarketChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newMarketIndex = Number.parseInt(event.target.value)
    onMarketChange(newMarketIndex)
  }

  const handleSportsbookChange = (sportsbook: Sportsbook, checked: boolean) => {
    setSelectedSportsbooks((prev) => {
      const newSet = new Set(prev)
      if (checked) {
        newSet.add(sportsbook)
      }
      else {
        newSet.delete(sportsbook)
      }
      return newSet
    })
  }

  // Check if any sportsbooks are selected
  const hasSelectedSportsbooks = selectedSportsbooks.size > 0

  return (
    <div className="@container bg-white rounded-lg shadow-xl overflow-hidden max-w-6xl mx-auto border border-gray-200 h-[500px] flex flex-col">
      {/* Fixed Header */}
      <div className="border-b border-gray-200 pt-2.5 px-4 @md:pt-7 @md:px-7 flex-shrink-0">
        <div className="flex flex-col gap-3 @min-[720px]:flex-row @min-[720px]:items-center @min-[720px]:justify-between @min-[720px]:gap-4">
          <h2 className="text-xl font-bold text-center @md:text-left">
            {leagueName}
          </h2>
          <select
            value={selectedMarketIndex}
            onChange={handleMarketChange}
            className="px-3 py-1 text-sm font-medium bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent @md:text-base"
          >
            {markets.map((market, index) => (
              <option key={market.marketName} value={index}>
                {market.name}
                {' '}
                {market.marketName}
              </option>
            ))}
          </select>
        </div>

        {/* Sportsbook Filter */}
        <div className="mt-3">
          <div className="flex items-center gap-2 @md:gap-3 flex-wrap justify-center @md:justify-start">
            {availableSportsbooks.map(sportsbook => (
              <SportsbookCheckbox
                key={sportsbook}
                sportsbook={sportsbook}
                isSelected={selectedSportsbooks.has(sportsbook)}
                onChange={handleSportsbookChange}
              />
            ))}
          </div>
        </div>

        <div className="h-3 @md:h-5" />
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto px-4 divide-y-1 divide-gray-200">
        {!hasSelectedSportsbooks
          ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center text-gray-500">
                  <p className="text-lg font-medium">No sportsbooks selected</p>
                  <p className="text-sm">Please select at least one sportsbook to view odds</p>
                </div>
              </div>
            )
          : (
              (widgetData?.selections || []).map((player, playerIndex) => {
                // Filter available sportsbooks for this player based on selection
                const filteredSportsbooks = availableSportsbooks
                  .filter((sportsbook) => {
                    const odds = player.odds[sportsbook]
                    return odds && selectedSportsbooks.has(sportsbook)
                  })

                return (
                  <div key={player.name} className="px-2 @md:pl-6 @md:pr-2 py-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="font-bold text-gray-900 text-sm @md:text-base">
                          {player.name}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        {filteredSportsbooks.length > 0
                          ? filteredSportsbooks.map((sportsbook) => {
                              const config = sportsbookConfig[sportsbook]
                              const odds = player.odds[sportsbook]
                              const oddsValue = oddsFormat === 'american' ? odds!.american : odds!.decimal
                              const LogoComponent = config.logo

                              return (
                                <button
                                  key={sportsbook}
                                  onClick={() => onSelectionClick(playerIndex, sportsbook)}
                                  aria-label={`Place bet with ${sportsbook} at ${oddsValue} odds`}
                                  className={`${config.color} overflow-hidden rounded text-sm font-medium flex flex-col items-center gap-1 pb-1 @md:flex-row @md:p-0 @md:pr-3.5 @md:gap-4 transition-all duration-150 hover:opacity-80 active:scale-95 cursor-pointer`}
                                  type="button"
                                >
                                  <LogoComponent className="size-12 md:size-10" />
                                  <span className="font-bold text-[10px] @md:text-base @md:w-16">
                                    {oddsValue}
                                  </span>
                                </button>
                              )
                            })
                          : (
                              <div className="h-[70px] @md:h-10 flex items-center">
                                <span className="text-sm text-gray-400 italic">No odds available</span>
                              </div>
                            )}
                      </div>
                    </div>
                  </div>
                )
              })
            )}
      </div>

      <div className="p-4 flex-shrink-0 border-t border-gray-200 flex items-center justify-end @md:px-6">
        <select
          value={oddsFormat}
          onChange={handleOddsFormatChange}
          className="px-2 py-1 text-[8px] @md:text-xs font-medium bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent @md:px-3"
        >
          <option value="american">American</option>
          <option value="decimal">Decimal</option>
        </select>
      </div>
    </div>
  )
}

export default Widget
